/**
 * Role-Based Access Control (RBAC) System for Redwood R2R Platform
 *
 * Defines user roles, permissions, and access control logic throughout the platform.
 * Ensures secure, granular control over features and data access.
 *
 * Security Architecture:
 * - Hierarchical role system with inheritance
 * - Granular permission controls
 * - Project-level and global-level permissions
 * - Fail-safe defaults (deny by default)
 *
 * Integration Points:
 * - Authentication context validation
 * - API endpoint protection
 * - UI component visibility control
 * - Data export restrictions
 */
import {UserPermissions, UserRole} from "@shared/interfaces/user.interface.ts";




/**
 * Role hierarchy and permissions matrix
 * super_admin: Full access to everything across all projects
 * owner: Full access to owned projects, can manage collaborators
 * collaborator: Can edit and export but cannot manage users or delete
 * viewer: Read-only access, can view but cannot edit or export
 */
export const ROLE_PERMISSIONS: Record<UserRole, UserPermissions> = {
  super_admin: {
    canEdit: true,
    canInvite: true,
    canView: true,
    canExport: true,
    canDelete: true,
    canManageUsers: true,
    canAccessAdmin: true,
    canCreateProjects: true,
    canViewAllProjects: true,
  },
  owner: {
    canEdit: true,
    canInvite: true,
    canView: true,
    canExport: true,
    canDelete: true,
    canManageUsers: true,
    canAccessAdmin: false,
    canCreateProjects: true,
    canViewAllProjects: false,
  },
  collaborator: {
    canEdit: true,
    canInvite: false,
    canView: true,
    canExport: true,
    canDelete: false,
    canManageUsers: false,
    canAccessAdmin: false,
    canCreateProjects: false,
    canViewAllProjects: false,
  },
  viewer: {
    canEdit: false,
    canInvite: false,
    canView: true,
    canExport: false,
    canDelete: false,
    canManageUsers: false,
    canAccessAdmin: false,
    canCreateProjects: false,
    canViewAllProjects: false,
  },
};

export class AccessControl {
  /**
   * Check if the user can edit content based on their role
   */
  static canEdit(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canEdit;
  }

  /**
   * Check if the user can invite others to projects
   */
  static canInvite(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canInvite;
  }

  /**
   * Check if the user can view content
   */
  static canView(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canView;
  }

  /**
   * Check if the user can export reports and presentations
   */
  static canExport(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canExport;
  }

  /**
   * Check if the user can delete projects or assessments
   */
  static canDelete(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canDelete;
  }

  /**
   * Check if the user can manage other users
   */
  static canManageUsers(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canManageUsers;
  }

  /**
   * Check if the user can access the admin dashboard
   */
  static canAccessAdmin(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canAccessAdmin;
  }

  /**
   * Check if the user can create new projects
   */
  static canCreateProjects(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canCreateProjects;
  }

  /**
   * Check if the user can view all projects (super admin only)
   */
  static canViewAllProjects(role: UserRole): boolean {
    return ROLE_PERMISSIONS[role].canViewAllProjects;
  }

  /**
   * Get all permissions for a role
   */
  static getPermissions(role: UserRole): UserPermissions {
    return ROLE_PERMISSIONS[role];
  }

  /**
   * Check if user has elevated privileges (super_admin or owner)
   */
  static hasElevatedAccess(role: UserRole): boolean {
    return role === 'super_admin' || role === 'owner';
  }

  /**
   * Check if the user is read-only
   */
  static isReadOnly(role: UserRole): boolean {
    return role === 'viewer';
  }

  /**
   * Get a role display name
   */
  static getRoleDisplayName(role: UserRole): string {
    const roleNames = {
      super_admin: 'Super Admin',
      owner: 'Owner',
      collaborator: 'Collaborator',
      viewer: 'Viewer'
    };
    return roleNames[role];
  }

  /**
   * Get role badge color for UI display
   */
  static getRoleBadgeColor(role: UserRole): string {
    const colors = {
      super_admin: 'bg-red-500 text-white',
      owner: 'bg-primary text-primary-foreground',
      collaborator: 'bg-blue-500 text-white',
      viewer: 'bg-gray-500 text-white'
    };
    return colors[role];
  }

  /**
   * Validate role string
   */
  static isValidRole(role: string): role is UserRole {
    return ['super_admin', 'owner', 'collaborator', 'viewer'].includes(role);
  }

  /**
   * Log an unauthorized access attempt
   */
  static async logUnauthorizedAccess(
    projectId: string,
    userId: string,
    attemptedAction: string,
    currentRole: UserRole
  ): Promise<void> {
    try {
      await fetch('/api/activity-log', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          project_id: projectId,
          user_id: userId,
          action: 'unauthorized_access_attempt',
          module_type: 'security',
          metadata: {
            attempted_action: attemptedAction,
            current_role: currentRole,
            timestamp: new Date().toISOString()
          }
        })
      });
    } catch (error) {
      console.error('Failed to log unauthorized access:', error);
    }
  }
}

/**
 * Hook for checking permissions in React components
 */
export function usePermissions(role: UserRole | null) {
  if (!role) {
    return {
      canEdit: false,
      canInvite: false,
      canView: false,
      canExport: false,
      canDelete: false,
      canManageUsers: false,
      canAccessAdmin: false,
      canCreateProjects: false,
      canViewAllProjects: false,
    };
  }

  return AccessControl.getPermissions(role);
}

/**
 * Utility function for checking if an action requires permission
 */
export function requiresPermission(
  role: UserRole | null,
  permission: keyof UserPermissions
): boolean {
  if (!role) return false;
  return AccessControl.getPermissions(role)[permission];
}